import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenancy } from '@/contexts/TenancyContext';
import { DataTable } from '@/components/ui/datatable';
import type { ColumnDef } from '@tanstack/react-table';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import HelpPopoverHover from '@/components/HelpPopoverHover';
import { getHelpContentByPage } from '@/services/HelpPopoverService';
import { listSOC2ControlMatrix, SOC2ControlMatrixEntry } from '@/services/soc2ControlMatrixService';
import { toast } from 'sonner';
import { ExternalLink, RefreshCcw, Search, Loader2 } from 'lucide-react';
import { routePathByName } from '@/app/routes';

import './soc2-control-matrix.css';

const buildAssuranceLink = (assuranceId: string) => {
  const target = routePathByName('assurance-library');
  return `${target}?assurance=${encodeURIComponent(assuranceId)}`;
};

const buildControlLink = (controlId: string) => {
  const target = routePathByName('soa');
  return `${target}?control=${encodeURIComponent(controlId)}`;
};

const columns: ColumnDef<SOC2ControlMatrixEntry>[] = [
  {
    accessorKey: 'controlID',
    header: 'SOC 2 Control',
    cell: ({ row }) => {
      const controlId = row.original.controlID;
      const href = buildControlLink(controlId);
      return (
        <Button variant="link" className="px-0" onClick={() => window.open(href, '_blank', 'noopener,noreferrer')}>
          {controlId}
          <ExternalLink className="ml-1 h-3.5 w-3.5" />
        </Button>
      );
    },
  },
  {
    accessorKey: 'suggestedAssurance',
    header: 'Suggested Assurance Activities',
    cell: ({ row }) => {
      const suggestions = row.original.suggestedAssurance;
      if (!suggestions.length) {
        return <span className="text-muted-foreground">No mapped assurance tasks</span>;
      }

      return (
        <div className="flex flex-wrap gap-2">
          {suggestions.map(item => (
            <Badge
              key={item}
              variant="outline"
              className="cursor-pointer transition-colors hover:bg-primary/10"
              onClick={() => window.open(buildAssuranceLink(item), '_blank', 'noopener,noreferrer')}
            >
              {item}
              <ExternalLink className="ml-1 h-3 w-3" />
            </Badge>
          ))}
        </div>
      );
    },
  },
];

const SOC2ControlMatrixPage: React.FC = () => {
  const { activeTenancy } = useTenancy();
  const navigate = useNavigate();
  const [matrix, setMatrix] = useState<SOC2ControlMatrixEntry[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [helpContent, setHelpContent] = useState<Record<string, any> | null>(null);

  const loadMatrix = async () => {
    try {
      setIsLoading(true);
      if (!activeTenancy?.id) return;
      const entries = await listSOC2ControlMatrix(activeTenancy.id);
      setMatrix(entries);
    } catch (error) {
      console.error('[SOC2ControlMatrix] failed to load matrix', error);
      toast.error('Failed to load SOC 2 control mappings');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMatrix();
  }, [activeTenancy?.id]);

  useEffect(() => {
    getHelpContentByPage('soc2.control.matrix').then(setHelpContent);
  }, []);

  const filteredMatrix = React.useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) {
      return matrix;
    }

    return matrix.filter(entry => {
      const matchControl = entry.controlID.toLowerCase().includes(term);
      const matchAssurance = entry.suggestedAssurance.some(id => id.toLowerCase().includes(term));
      return matchControl || matchAssurance;
    });
  }, [searchTerm, matrix]);

  return (
    <div className="page-container">
      <div className="glass-card">
        <div className="header-row flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
          <div>
            <h2 className="title flex flex-row items-center gap-2">
              SOC 2 Control Matrix
              <span className="flex items-center bg-transparent pl-1 rounded font-semibold text-center">
                <HelpPopoverHover config={helpContent?.['page.title']} />
              </span>
            </h2>
            <p className="subtitle">
              Map SOC 2 Trust Services Criteria to assurance activities and implementation owners.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 md:gap-3 md:flex-nowrap">
            <Button
              variant="outline"
              size="sm"
              onClick={loadMatrix}
              disabled={isLoading}
              className="flex items-center"
            >
              <RefreshCcw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(routePathByName('assurance-library'))}
            >
              View Assurance Library
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(routePathByName('soa'))}
            >
              Open SOA
            </Button>
          </div>
        </div>

        <div className="filters-row mt-6 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <div className="relative w-full md:w-[320px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={event => setSearchTerm(event.target.value)}
                placeholder="Search controls or assurance IDs"
                className="pl-9"
              />
            </div>
          </div>
        </div>

        <div className="mt-6">
          {isLoading ? (
            <div className="flex items-center gap-2 rounded-lg border border-dashed border-border/60 p-6 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading SOC 2 controls…
            </div>
          ) : filteredMatrix.length ? (
            <DataTable
              columns={columns}
              data={filteredMatrix}
              pageSize={10}
              tableId="soc2-control-matrix"
            />
          ) : (
            <div className="rounded-lg border border-dashed border-border/60 p-6 text-sm text-muted-foreground">
              No SOC 2 controls found.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SOC2ControlMatrixPage;
